import { useNavigate } from "react-router-dom";
import { Badge, Button, DescriptionList, Dot, LinkButton, Modal, ModalFooter, Notice, Row } from "@plataforma/design-system";
import type { Appointment, AppointmentStatus, Invoice } from "@plataforma/shared";
import { useAppConfig } from "../../app/config";
import { whatsappUrl } from "../../lib/format";
import { appointmentStatusLabel, appointmentStatusTone } from "../../lib/labels";

const order: Record<AppointmentStatus, AppointmentStatus[]> = {
  scheduled: ["confirmed", "completed", "no_show", "cancelled"],
  confirmed: ["completed", "no_show", "cancelled", "scheduled"],
  completed: ["confirmed", "scheduled", "no_show", "cancelled"],
  cancelled: ["scheduled", "confirmed", "completed", "no_show"],
  no_show: ["scheduled", "confirmed", "completed", "cancelled"],
};
const verb = (target: AppointmentStatus, current: AppointmentStatus) =>
  ({ scheduled: "Voltar para agendada", confirmed: current === "scheduled" ? "Confirmar" : "Voltar para confirmada", completed: "Marcar como realizada", cancelled: "Cancelar consulta", no_show: "Não compareceu" })[target];

interface Props {
  appointment: Appointment;
  invoice?: Invoice;
  locationColor: string;
  onStatus: (status: AppointmentStatus) => void;
  onEdit: () => void;
  onInvoice: (invoice: Invoice, step: "emit" | "view") => void;
  onClose: () => void;
}

export function AppointmentDetail({ appointment, invoice, locationColor, onStatus, onEdit, onInvoice, onClose }: Props) {
  const { format, has } = useAppConfig();
  const navigate = useNavigate();
  const status = appointment.status;
  return (
    <Modal title="Detalhes da consulta" onClose={onClose}>
      <Row align="between" style={{ marginBottom: 16 }}>
        <div>
          <div className="ds-h2">{appointment.clientName}</div>
          <div className="ds-muted ds-small">{appointment.type}</div>
        </div>
        <Badge tone={appointmentStatusTone[status]}>{appointmentStatusLabel[status]}</Badge>
      </Row>
      <DescriptionList items={[
        { term: "Data e horário", value: `${format.longDate(appointment.date)} · ${appointment.time}` },
        { term: "Duração", value: `${appointment.durationMinutes} minutos` },
        { term: "Local", value: <><Dot color={locationColor} />{appointment.locationName}</> },
        { term: "Valor combinado", value: format.money(appointment.value) },
        { term: "Contato", value: appointment.clientPhone },
        { term: "WhatsApp", value: <LinkButton size="small" href={whatsappUrl(appointment.clientPhone)} target="_blank" rel="noopener">Abrir conversa</LinkButton> },
      ]} />
      {status === "completed" && invoice ? (
        <Notice tone={invoice.status === "pending" ? "warning" : "info"} action={invoice.status === "pending" ? <Button size="small" variant="primary" onClick={() => onInvoice(invoice, "emit")}>Emitir nota</Button> : <Button size="small" onClick={() => onInvoice(invoice, "view")}>Ver nota</Button>}>
          {invoice.status === "pending" ? "A nota fiscal ainda não foi emitida." : `Nota ${invoice.number} ${invoice.status === "cancelled" ? "cancelada" : "emitida"}.`}
        </Notice>
      ) : null}
      <p className="ds-muted ds-small" style={{ margin: "16px 0 8px" }}>Alterar situação</p>
      <Row>
        {order[status].map((target, index) => (
          <Button key={target} size="small" variant={index === 0 && (status === "scheduled" || status === "confirmed") ? "primary" : "default"} tone={target === "cancelled" ? "danger" : "default"} onClick={() => onStatus(target)}>
            {verb(target, status)}
          </Button>
        ))}
      </Row>
      <ModalFooter start={<><Button size="small" onClick={onEdit}>Editar / remarcar</Button>{has("clients") ? <Button size="small" onClick={() => { onClose(); navigate(`/clientes/${appointment.clientId}`); }}>Abrir perfil</Button> : null}</>}>
        <Button onClick={onClose}>Fechar</Button>
      </ModalFooter>
    </Modal>
  );
}
